import {
  Analysis,
  AnalysisPolygon,
  FileOverviewItem,
  AnalysisPlanning,
  PlanningChartData,
} from '@shared/models/analysis.model';

/**
 * Unified representation of any data section in the store.
 * Each section (detail, files, polygons, plannings) follows this shape.
 */
export interface AnalysisStoreSection<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  invalidated: boolean;
  etag?: string;
}

/**
 * Cache entry for any data section
 */
export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
  etag?: string;
}

/**
 * Request to cascade-invalidate multiple sections
 */
export interface InvalidationRequest {
  sections: AnalysisSectionType[];
  reason?: InvalidationReason;
  source?: string;
}

/**
 * Type of data section in the store
 */
export type AnalysisSectionType = 'detail' | 'files' | 'polygons' | 'plannings';

/**
 * Reason for invalidation (used for logging/debugging)
 */
export type InvalidationReason =
  | 'sse_update'
  | 'manual_sync'
  | 'polygon_change'
  | 'status_change'
  | 'workflow_advance';

/**
 * Result of loading a section
 */
export interface SectionLoaderResult {
  success: boolean;
  section: AnalysisSectionType;
  timestamp: number;
  durationMs: number;
  error?: string;
}

/**
 * SSE event emitted from backend about analysis updates
 */
export interface AnalysisSSEEvent {
  type: 'analysis_updated' | 'detail_refreshed' | 'error' | 'ping';
  timestamp?: string;
  // Status fields (always present)
  technicalStatus?: Analysis['technicalStatus'];
  businessStatus?: Analysis['businessStatus'];
  // Optional metadata about what changed (for cascade invalidation)
  changes?: {
    technical_status?: boolean;
    files_updated?: boolean;
    polygons_updated?: boolean;
    plannings_outdated?: boolean;
  };
  reason?: string;
  // Full detail for major updates (optional)
  fullDetail?: Analysis;
}

/**
 * Parameters for section loading
 */
export interface SectionLoadParams {
  organizationId: string;
  analysisId: string;
  forceRefresh?: boolean;
}

/**
 * State of a single data section
 */
export interface DetailSectionState {
  data: Analysis | null;
  loading: boolean;
  invalidated: boolean;
  error: string | null;
}

export interface FilesSectionState {
  data: FileOverviewItem[] | null;
  loading: boolean;
  invalidated: boolean;
  error: string | null;
}

export interface PolygonsSectionState {
  data: AnalysisPolygon[] | null;
  loading: boolean;
  invalidated: boolean;
  error: string | null;
}

export interface PlanningsSectionState {
  data: AnalysisPlanning[] | null;
  chartDataCache: Record<string, PlanningChartData>;
  loadingChartData: boolean;
  loading: boolean;
  invalidated: boolean;
  error: string | null;
}

/**
 * Complete store state (composed from all sections)
 */
export interface AnalysisDetailStoreState {
  // Detail section
  detail: Analysis | null;
  detailLoading: boolean;
  detailInvalidated: boolean;
  detailError: string | null;

  // Files section
  filesOverview: FileOverviewItem[] | null;
  filesLoading: boolean;
  filesInvalidated: boolean;
  filesError: string | null;

  // Polygons section
  polygons: AnalysisPolygon[] | null;
  polygonsLoading: boolean;
  polygonsInvalidated: boolean;
  polygonsError: string | null;

  // Plannings section
  plannings: AnalysisPlanning[] | null;
  planningChartDataCache: Record<string, PlanningChartData>;
  planningsLoading: boolean;
  planningsLoadingChartData: boolean;
  planningsInvalidated: boolean;
  planningsError: string | null;

  // System state
  lastSyncAt: string | null;
  polling: boolean;
  sseContext: { organizationId: string; analysisId: string } | null;
  pageVisible: boolean;
  error: string | null;
  lastLoadedId: string | null;
}

/**
 * Constants
 */
export const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
